<template>
  <v-row justify="center">
    <v-dialog v-model="show" max-width="400px" persistent>
      <validation-observer ref="form">
        <v-card>
          <v-card-title style="justify-content: center;">{{$t("product.selectStockPosition")}}</v-card-title>
          <v-container fluid>
            <v-card-text class="mb-1" v-if="remainingQuantityToPick > 0 && filteredChoices.length > 0">
              {{ $t("product.selectStockPositionSentence") }}
            </v-card-text>
            <v-card-text class="mb-1" v-if="remainingQuantityToPick > 0 && filteredChoices.length <= 0">
              {{ $t("product.typeProductPositionSentence") }}
            </v-card-text>
            <v-select
              v-if="remainingQuantityToPick > 0 && filteredChoices.length > 0"
              v-model="selectedChoice"
              :items="filteredChoices"
              :label="$t('components.modalPosition.selectPosition')"
              :no-data-text="$t('components.modalPosition.selectPosition')"
              :item-text="item => item.position_details.map(detail => detail.name + ' ' + detail.value).join(' - ')"
              :item-value="item => item"
              clearable
              dense
              hide-details
              outlined
              :loading="loading"
              required
              append-outer-icon='mdi-check'
              @click:append-outer='addNewPositionToProduct()'
            >
            </v-select>
            <v-row v-if="remainingQuantityToPick > 0" justify='center' class='mt-1'>
              <a target="_blank" style="text-transform: none;text-decoration: underline;"
                 v-if="filteredChoices.length > 0"
                 @click="toggleDisplayFreeEntryOfPosition"
              >
                  {{$t("product.positionNotFound")}}
              </a>
              <v-text-field
                v-if="displayFreeEntryOfPosition || filteredChoices.length === 0"
                v-model="freeChoice"
                :label="$t('product.customPosition')"
                outlined
                dense
                append-outer-icon='mdi-check'
                class="px-3 pt-2"
                @click:append-outer='addNewFreeEntryOfPosition()'
                hide-details="auto"
              ></v-text-field>
            </v-row>
            <v-card-text v-if="product.selected_product_positions.length" class="pt-1">{{$t("product.preciseTakenQuantity")}}</v-card-text>
            <v-row v-for="(position, index) in product.selected_product_positions" v-bind:key="index">
              <v-col :cols="productQuantityToHandle > 1 ? 6 : 10" class='py-0' align-self='center'>
                <v-card-text class="pt-0">
                  {{ position.custom.map(detail => (detail.name === "customPosition" ? "" : detail.name) + ' ' + detail.value).join(' - ') }}
                </v-card-text>
              </v-col>
              <v-col v-if="productQuantityToHandle > 1" cols="4" class='pt-0' align-self='center'>
                <validation-provider
                  class="width-100" name ="taken quantity" v-slot="{ errors }"
                  rules="min_value:1|integer|required"
                >
                  <v-text-field
                    hide-details="auto" type="number" class="ml-2 mr-2 pa-0" v-model="position.taken_quantity" outlined
                    dense :error-messages="errors" :label="$t('product.qty')"
                  >
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="2" align-self='center' class='pt-0'>
                <v-btn icon class="pa-0" color="error" @click='deletePosition(index)'>
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
          </v-row>
            <v-card-text
              class="pb-0 primary--text" style=" display: flex; justify-content: center;"
            >
              <span v-if='remainingQuantityToPick >= 0' >
                {{ $t("product.remainingQuantityToPick") }}: {{ remainingQuantityToPick }}
              </span>
              <span v-else class="pb-0 error--text">
                {{ $t("product.tooMuchQuantityPicked") }}
              </span>
            </v-card-text>
          </v-container>
          <v-card-actions class="pa-0">
            <v-btn
              color="primary"
              tile
              style="width: 100%"
              :disabled="remainingQuantityToPick !== 0"
              @click="confirm()"
            >{{$t('subOrder.confirm')}}</v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>

    </v-dialog>
  </v-row>
</template>


<script>
  import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
  import { min_value, required, integer} from 'vee-validate/dist/rules'
  import { EventBus } from '@/event-bus'

  // Install rules used
  extend("min_value", {
    ...min_value,
  })
  extend('integer', {
    ...integer,
    message: "floating point numbers are not allowed"
  })
  extend('required', {
    ...required,
    message: 'This field is required'
  })
  export default {
    name: "ProductPositionChoiceDialog",
    components: {
      ValidationProvider,
      ValidationObserver
    },
    data() {
      return {
        displayFreeEntryOfPosition: false,
        selectedChoice: undefined,
        freeChoice: undefined,
        scan: ""
      }
    },
    mounted() {
      this.addEventKeyDown()
    },
    watch: {
      value: function(newValue){
        if(newValue === true) {
          this.addEventKeyDown()
        } else {
          this.removeEventKeyDown()
        }
      }
    },
    props: {
      title: {
        type: String,
        default: undefined
      },
      value: {
        type: Boolean,
        default: false
      },
      choices: {
        type: Array,
        required: true,
      },
      product: {
        type: Object,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit("input", value);
        }
      },
      productQuantityToHandle: function() {
        return this.product.current_status === 'validated' ? this.product.validated_quantity : this.product.product_quantity
      },
      remainingQuantityToPick: function(){
        let remainingQuantityToPick = this.productQuantityToHandle - this.product.selected_product_positions.reduce(
          (productValidatedQuantity, position) => parseInt(productValidatedQuantity) + parseInt(position.taken_quantity), 0
        )
        return remainingQuantityToPick
      },
      filteredChoices: function(){
        if(this.remainingQuantityToPick <= 0) {
          return []
        } else {
          let alreadySelectedPosition = this.product.selected_product_positions.map(position => position.product_position_id)
          return this.choices.filter(choice => !alreadySelectedPosition.includes(choice.position_id) && choice.stock > 0)
        }
      }
    },
    methods: {
      confirm: function() {
        this.$refs.form.validate().then(success => {
          if (success) {
            this.$emit("confirm", this.product)
            this.show = false
          } else {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t("location.checkFormFields"),
              color: "error"
            })
          }
        })
      },
      addNewPositionToProduct: function() {
        if(this.selectedChoice){
          this.product.selected_product_positions.push({
            product_position_id: this.selectedChoice.position_id.toString(),
            custom:  this.convertToString(this.selectedChoice.position_details),
            taken_quantity:  this.selectedChoice.stock > this.remainingQuantityToPick ? this.remainingQuantityToPick : this.selectedChoice.stock,
            barcode: this.selectedChoice.barcode
          })
          this.selectedChoice = undefined
        } else {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('components.modalPosition.noPositionSelected'),
            color: "error"
          })
        }
      },
      addNewFreeEntryOfPosition: function() {
        //both checks in case of feeChoice is undefined or else
        if(this.freeChoice && this.freeChoice.trim()){
          this.product.selected_product_positions.push({
            product_position_id: this.freeChoice,
            custom: [{name:"customPosition", value: this.freeChoice}],
            taken_quantity:  1
          })
          this.freeChoice = undefined
          this.displayFreeEntryOfPosition = false
        } else {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('components.modalPosition.noPositionSelected'),
            color: "error"
          })
        }
      },
      deletePosition: function(indexPosition) {
        this.product.selected_product_positions.splice(indexPosition, 1)
      },
      toggleDisplayFreeEntryOfPosition(){
        this.displayFreeEntryOfPosition = !this.displayFreeEntryOfPosition
      },
      addEventKeyDown : function (){
        document.addEventListener("keydown", this.convertSpecialCharToNumber);
      },
      removeEventKeyDown : function (){
        document.removeEventListener("keydown", this.convertSpecialCharToNumber);
      },
      convertSpecialCharToNumber : function(event){
        let charCode = (event.charCode) ? event.charCode : event.keyCode
        if(charCode !== 13){
          this.scan += String.fromCharCode(charCode)
        }
        if(charCode == 13 && this.scan.trim() != ""){
          this.checkPositionsForScan(this.scan)
          this.scan = "";
        }
      },
      checkPositionsForScan(barcode){
        let positionExists = false

        let existingPosition = this.product.selected_product_positions.find(
          alreadySelectedPosition => alreadySelectedPosition.barcode == barcode
        )
        if(existingPosition){
          existingPosition.taken_quantity ++
          positionExists = true
        } else {
          for (let position of this.filteredChoices){
            if(position.barcode == barcode){
              this.selectedChoice = position
              this.addNewPositionToProduct()
              positionExists = true
              if (this.productQuantityToHandle === 1 && this.choices.length < 2) {
                this.$emit("confirm", this.product)
                this.show = false
              }
              break
            }
          }
        }
        if(!positionExists){
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t("components.modalPosition.positionCanNotBeSelected"),
            color: "error"
          })
        }
      },
      convertToString(customs) {
        for(let custom of customs) {
          custom.name = custom.name.toString()
          custom.value = custom.value.toString()
        }
        return customs
      }
    }
  }
</script>
